import React from 'react';
import { Edit, Trash2, Globe, Palette } from 'lucide-react';
import Button from '../UI/Button';
import { Flavour } from '../../types';

interface AppCardProps {
  flavour: Flavour;
  isCurrentApp: boolean;
  isSuperAdmin: boolean;
  onEdit: (flavour: Flavour) => void;
  onDelete: (flavourId: string) => void;
  onSwitch: (flavourId: string) => void;
  canSwitch: boolean;
}

const AppCard: React.FC<AppCardProps> = ({
  flavour,
  isCurrentApp,
  isSuperAdmin,
  onEdit,
  onDelete,
  onSwitch,
  canSwitch,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'review':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'review':
        return 'Under Review';
      case 'draft':
        return 'Draft';
      case 'inactive':
        return 'Inactive';
      default:
        return status;
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 p-6 transition-all ${
        isCurrentApp
          ? 'border-primary-300 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            {flavour.appIconUrl ? (
              <img 
                src={flavour.appIconUrl} 
                alt={`${flavour.name} icon`}
                className="h-5 w-5 object-cover rounded"
                onError={(e) => {
                  // Fallback to Globe icon if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <Globe className={`h-5 w-5 text-primary ${flavour.appIconUrl ? 'hidden' : ''}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{flavour.name}</h3>
            <div className="flex items-center space-x-2 mt-1">
              {isCurrentApp && (
                <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                  Current App
                </span>
              )}
              {!isCurrentApp && (
                <span className={`text-xs px-2 py-1 rounded-full flex items-center ${getStatusColor(flavour.status || 'draft')}`}>
                  {getStatusText(flavour.status || 'draft')}
                </span>
              )}
            </div>
          </div>
        </div>
        {isSuperAdmin && (
          <div className="flex space-x-2">
            <button
              onClick={() => onEdit(flavour)}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Edit app"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(flavour.id)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete app"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {flavour.description && (
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{flavour.description}</p>
      )}

      {flavour.packageName && (
        <p className="text-xs text-gray-500 mb-4">
          Package: <code className="bg-gray-100 px-1 rounded font-mono">{flavour.packageName}</code>
        </p>
      )}

      {/* Theme Colors Preview */}
      {flavour.themeColors && Object.keys(flavour.themeColors).length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Palette className="h-4 w-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">Theme Colors</span>
          </div>
          <div className="flex space-x-1">
            {Object.entries(flavour.themeColors).slice(0, 6).map(([key, color]) => (
              <div
                key={key}
                className="w-6 h-6 rounded border border-gray-300"
                style={{ backgroundColor: color }}
                title={`${key}: ${color}`}
              />
            ))}
            {Object.keys(flavour.themeColors).length > 6 && (
              <div className="w-6 h-6 rounded border border-gray-300 bg-gray-100 flex items-center justify-center">
                <span className="text-xs text-gray-500">+{Object.keys(flavour.themeColors).length - 6}</span>
              </div>
            )}
          </div>
        </div>
      )}


      {flavour.status !== 'draft' && canSwitch && !isCurrentApp && (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSwitch(flavour.id)}
            className="flex-1"
          >
            Switch to App
          </Button>
        </div>
      )}
    </div>
  );
};

export default AppCard; 